import type { PublicProfile } from '@/types'
import ProfileBranding from '@/components/profile/ProfileBranding'
import { Mail, Phone, MapPin, Linkedin, Github, Globe, Download } from 'lucide-react'

interface ClassicTemplateProps {
  profile: PublicProfile
}

export default function ClassicTemplate({ profile }: ClassicTemplateProps) {
  const { visibility } = profile.profileSettings
  const fullName = `${profile.firstName} ${profile.lastName}`

  return (
    <div className="min-h-screen bg-neutral-100 py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-6">
        {/* Print-style paper */}
        <div className="bg-white shadow-xl" style={{ minHeight: '11in' }}>
          {/* Header - Traditional Resume Style */}
          <div className="border-b-4 border-gray-900 px-12 pt-12 pb-8">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-3 tracking-tight">
              {fullName}
            </h1>
            {profile.title && (
              <p className="text-xl font-serif text-gray-700 mb-4">{profile.title}</p>
            )}

            {/* Contact bar */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600 border-t border-gray-300 pt-4 mt-4">
              {profile.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  <span>{profile.location}</span>
                </div>
              )}
              {visibility.showContact && profile.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4" />
                  <span>{profile.email}</span>
                </div>
              )}
              {visibility.showContact && profile.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-4 h-4" />
                  <span>{profile.phone}</span>
                </div>
              )}
              {visibility.showContact && profile.linkedInUrl && (
                <div className="flex items-center gap-1.5">
                  <Linkedin className="w-4 h-4" />
                  <span>LinkedIn</span>
                </div>
              )}
              {visibility.showContact && profile.githubUrl && (
                <div className="flex items-center gap-1.5">
                  <Github className="w-4 h-4" />
                  <span>GitHub</span>
                </div>
              )}
              {visibility.showContact && profile.portfolioUrl && (
                <div className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4" />
                  <span>Portfolio</span>
                </div>
              )}
            </div>
          </div>

          {/* Main content */}
          <div className="px-12 py-10">
            {/* Professional Summary */}
            {profile.bio && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-3 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Professional Summary
                </h2>
                <p className="text-gray-700 leading-relaxed font-serif">{profile.bio}</p>
              </div>
            )}

            {/* Experience */}
            {visibility.showExperience && profile.experience && profile.experience.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-4 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Professional Experience
                </h2>
                <div className="space-y-5">
                  {profile.experience.map((exp, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className="font-serif font-bold text-gray-900 text-lg">
                          {exp.jobTitle}
                        </h3>
                        <span className="text-sm text-gray-600 font-serif whitespace-nowrap ml-4">
                          {exp.startDate} – {exp.isCurrent ? 'Present' : exp.endDate}
                        </span>
                      </div>
                      <p className="font-serif italic text-gray-700 mb-2">
                        {exp.companyName}
                        {exp.location && ` | ${exp.location}`}
                      </p>
                      {exp.description && (
                        <p className="text-gray-700 leading-relaxed font-serif text-sm">
                          {exp.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {visibility.showEducation && profile.education && profile.education.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-4 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Education
                </h2>
                <div className="space-y-4">
                  {profile.education.map((edu, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className="font-serif font-bold text-gray-900">{edu.degree}</h3>
                        <span className="text-sm text-gray-600 font-serif whitespace-nowrap ml-4">
                          {edu.startDate} – {edu.endDate}
                        </span>
                      </div>
                      <p className="font-serif italic text-gray-700">
                        {edu.institution}
                        {edu.fieldOfStudy && ` | ${edu.fieldOfStudy}`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {visibility.showProjects && profile.projects && profile.projects.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-4 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Projects
                </h2>
                <div className="space-y-5">
                  {profile.projects.map((project, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-baseline mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-serif font-bold text-gray-900">{project.title}</h3>
                          {project.isCurrent && (
                            <span className="text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-800 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        {(project.startDate || project.endDate) && (
                          <span className="text-sm text-gray-600 font-serif whitespace-nowrap ml-4">
                            {project.startDate || 'N/A'} –{' '}
                            {project.isCurrent ? 'Present' : project.endDate || 'N/A'}
                          </span>
                        )}
                      </div>
                      {project.description && (
                        <p className="text-gray-700 leading-relaxed font-serif text-sm mb-2">
                          {project.description}
                        </p>
                      )}
                      {project.technologies && (
                        <p className="text-sm text-gray-600 font-serif mb-2">
                          <span className="font-bold">Technologies:</span> {project.technologies}
                        </p>
                      )}
                      {(project.projectUrl || project.repositoryUrl) && (
                        <div className="flex gap-4 text-sm">
                          {project.projectUrl && (
                            <a
                              href={project.projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-700 underline font-serif hover:text-gray-900"
                            >
                              Live Demo
                            </a>
                          )}
                          {project.repositoryUrl && (
                            <a
                              href={project.repositoryUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-700 underline font-serif hover:text-gray-900"
                            >
                              Source Code
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {visibility.showSkills && profile.skills && profile.skills.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-3 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Skills
                </h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {profile.skills?.map((skill, index) => (
                    <span key={index} className="text-gray-700 font-serif">
                      {skill.skill?.name}
                      {index < (profile.skills?.length || 0) - 1 && ' •'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {visibility.showLanguages && profile.languages && profile.languages.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-3 pb-1 border-b-2 border-gray-900 uppercase tracking-wide">
                  Languages
                </h2>
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  {profile.languages?.map((lang, index) => (
                    <span key={index} className="text-gray-700 font-serif">
                      {lang.language?.name} ({lang.proficiencyLevel})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {visibility.showDocuments && profile.cvUrl && (
              <div className="mt-10 pt-6 border-t border-gray-300">
                <a
                  href={profile.cvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-serif hover:bg-gray-800 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Full CV</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subtle Branding */}
      <ProfileBranding variant="light" />
    </div>
  )
}
